import React, { useState, useRef, useEffect } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, User, Heart, ShoppingBag, LogOut, LayoutDashboard, LogIn } from "lucide-react";
import { useLang, LANGS } from "../lib/i18n";
import { useAuth } from "../lib/auth";
import CartDrawer from "./CartDrawer";

export default function Header() {
  const { t, lang, setLang } = useLang();
  const { user, isAdmin, logout, cartCount, favorites } = useAuth();
  const [open, setOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);
  const nav = useNavigate();
  const menuRef = useRef(null);
  const toggleRef = useRef(null);
  useEffect(() => {
    if (!open) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const close = (event) => { if (event.key === 'Escape') { setOpen(false); toggleRef.current?.focus(); } };
    const resize = () => { if (window.innerWidth >= 768) setOpen(false); };
    window.addEventListener('keydown', close);
    window.addEventListener('resize', resize);
    return () => { document.body.style.overflow = previous; window.removeEventListener('keydown', close); window.removeEventListener('resize', resize); };
  }, [open]);

  useEffect(() => {
    const onClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const navItems = [
    { to: "/", label: t("nav.home"), testid: "nav-home" },
    { to: "/catalog", label: t("nav.catalog"), testid: "nav-catalog" },
    { to: "/pdfs", label: t("nav.pdfs"), testid: "nav-pdfs" },
  ];

  const initials = user ? `${(user.name || "")[0] || ""}${(user.surname || "")[0] || ""}`.toUpperCase() : "";

  return (
    <header className="fd-glass-header sticky top-0 z-50" data-testid="site-header">
      <div className="fd-container flex items-center justify-between h-16 md:h-20">
        <Link to="/" className="flex items-center gap-2" data-testid="brand-logo">
          <div className="w-8 h-8 bg-neutral-900 flex items-center justify-center">
            <span className="text-white text-sm font-semibold tracking-tight">F</span>
          </div>
          <span className="text-lg font-medium tracking-tight text-neutral-900">Friend Doors</span>
        </Link>

        <nav className="hidden md:flex items-center gap-10">
          {navItems.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              data-testid={n.testid}
              className={({ isActive }) =>
                `text-sm tracking-wide transition-colors duration-200 ${isActive ? "text-neutral-900" : "text-neutral-500 hover:text-neutral-900"}`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <div className="hidden sm:flex items-center border border-neutral-200 rounded-sm overflow-hidden" data-testid="lang-switcher">
            {LANGS.map((l) => (
              <button
                key={l.code}
                onClick={() => setLang(l.code)}
                data-testid={`lang-${l.code}`}
                className={`px-3 py-1.5 text-xs font-medium transition-colors duration-200 ${
                  lang === l.code ? "bg-neutral-900 text-white" : "bg-white text-neutral-600 hover:text-neutral-900"
                }`}
              >
                {l.label}
              </button>
            ))}
          </div>

          {/* Cart shortcut — opens slide-over drawer */}
          <button type="button" onClick={() => setCartOpen(true)} className="relative p-2 text-neutral-700 hover:text-neutral-900 inline-flex" data-testid="header-cart" aria-label={t("auth.cart")}>
            <ShoppingBag size={18} />
            {cartCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 bg-neutral-900 text-white text-[10px] flex items-center justify-center rounded-full" data-testid="header-cart-count">{cartCount}</span>
            )}
          </button>

          {/* Auth area */}
          {!user ? (
            <>
              {/* Desktop: single "Daxil ol" button */}
              <div className="hidden sm:block">
                <Link to="/login" className="fd-btn-primary !py-2 !px-4 text-xs" data-testid="nav-login">
                  <LogIn size={12} /> {t("auth.login")}
                </Link>
              </div>
              {/* Mobile: profile icon that opens /login */}
              <Link to="/login" className="sm:hidden w-9 h-9 border border-neutral-300 text-neutral-700 hover:text-neutral-900 hover:border-neutral-900 flex items-center justify-center transition-colors" data-testid="mobile-profile-icon" aria-label={t("auth.login")}>
                <User size={16} />
              </Link>
            </>
          ) : (
            <div className="relative" ref={menuRef}>
              <button onClick={() => setMenuOpen((v) => !v)} className="w-9 h-9 rounded-full bg-neutral-900 text-white text-xs font-medium flex items-center justify-center hover:opacity-90" data-testid="user-menu-btn">
                {initials || <User size={16} />}
              </button>
              {menuOpen && (
                <div className="absolute right-0 mt-2 w-56 bg-white border border-neutral-200 shadow-lg py-2 z-50" data-testid="user-menu">
                  <div className="px-4 py-2 border-b border-neutral-100">
                    <p className="text-sm text-neutral-900 truncate">{user.name} {user.surname}</p>
                    <p className="text-xs text-neutral-500 truncate">{user.email}</p>
                  </div>
                  <Link to="/profile" onClick={() => setMenuOpen(false)} className="flex items-center gap-3 px-4 py-2.5 text-sm text-neutral-700 hover:bg-neutral-50" data-testid="menu-profile">
                    <User size={14} /> {t("auth.profile")}
                  </Link>
                  <Link to="/profile?tab=favorites" onClick={() => setMenuOpen(false)} className="flex items-center gap-3 px-4 py-2.5 text-sm text-neutral-700 hover:bg-neutral-50" data-testid="menu-favorites">
                    <Heart size={14} /> {t("auth.favorites")} {favorites.length > 0 && <span className="ml-auto text-xs text-neutral-400">{favorites.length}</span>}
                  </Link>
                  <button onClick={() => { setMenuOpen(false); setCartOpen(true); }} className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-neutral-700 hover:bg-neutral-50" data-testid="menu-cart">
                    <ShoppingBag size={14} /> {t("auth.cart")} {cartCount > 0 && <span className="ml-auto text-xs text-neutral-400">{cartCount}</span>}
                  </button>
                  {isAdmin && (
                    <Link to="/admin" onClick={() => setMenuOpen(false)} className="flex items-center gap-3 px-4 py-2.5 text-sm text-neutral-700 hover:bg-neutral-50 border-t border-neutral-100" data-testid="menu-admin">
                      <LayoutDashboard size={14} /> {t("auth.admin_panel")}
                    </Link>
                  )}
                  <button onClick={() => { setMenuOpen(false); logout(); nav("/"); }} className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-neutral-700 hover:bg-neutral-50 border-t border-neutral-100" data-testid="menu-logout">
                    <LogOut size={14} /> {t("auth.logout")}
                  </button>
                </div>
              )}
            </div>
          )}

          <button ref={toggleRef} className={`md:hidden mobile-menu-trigger ${open ? "is-open" : ""}`} onClick={() => { setMenuOpen(false); setOpen(!open); }} data-testid="mobile-menu-toggle" aria-expanded={open} aria-controls="mobile-navigation" aria-label={open ? "Menyunu bağla" : "Menyunu aç"}>
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden mobile-navigation-shell" data-testid="mobile-menu">
          <button className="mobile-navigation-backdrop" aria-label="Menyunu bağla" onClick={() => setOpen(false)} tabIndex={-1} />
          <nav id="mobile-navigation" aria-label="Əsas menyu" className="mobile-navigation-panel">
          
          <div className="mobile-menu-content flex flex-col gap-4">
            {!user && (
              <div className="grid grid-cols-2 gap-2 pb-3 border-b border-neutral-100">
                <Link to="/login" onClick={() => setOpen(false)} className="fd-btn-primary justify-center text-xs !py-2.5" data-testid="m-nav-login-top">
                  <LogIn size={12} /> {t("auth.login")}
                </Link>
                <Link to="/register" onClick={() => setOpen(false)} className="fd-btn-ghost justify-center text-xs !py-2.5" data-testid="m-nav-register-top">
                  {t("auth.register")}
                </Link>
              </div>
            )}
            {navItems.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.to === "/"} onClick={() => setOpen(false)} className={({ isActive }) => `mobile-navigation-link ${isActive ? "is-active" : ""}`} data-testid={`m-${n.testid}`}>
                <span>{n.label}</span>
              </NavLink>
            ))}
            <div className="flex gap-2 pt-5 border-t border-neutral-200 mt-2">
              {LANGS.map((l) => (
                <button key={l.code} onClick={() => setLang(l.code)} data-testid={`m-lang-${l.code}`}
                  aria-pressed={lang === l.code} className={`px-5 py-3 text-xs font-semibold rounded-lg border transition-colors ${lang === l.code ? "bg-neutral-900 text-white border-neutral-900" : "border-neutral-200 text-neutral-600 hover:bg-white"}`}>
                  {l.label}
                </button>
              ))}
            </div>
            {user && (
              <div className="pt-2 border-t border-neutral-100 flex flex-col gap-3">
                <p className="text-xs text-neutral-500">{user.email}</p>
                <Link to="/profile" onClick={() => setOpen(false)} className="text-sm text-neutral-700" data-testid="m-menu-profile">{t("auth.profile")}</Link>
                <Link to="/profile?tab=favorites" onClick={() => setOpen(false)} className="text-sm text-neutral-700" data-testid="m-menu-favorites">{t("auth.favorites")}</Link>
                {isAdmin && (
                  <Link to="/admin" onClick={() => setOpen(false)} className="text-sm text-neutral-700" data-testid="m-menu-admin">{t("auth.admin_panel")}</Link>
                )}
                <button onClick={() => { setOpen(false); logout(); nav("/"); }} className="text-left text-sm text-neutral-700" data-testid="m-menu-logout">{t("auth.logout")}</button>
              </div>
            )}
          </div>
          </nav>
        </div>
      )}
      <CartDrawer open={cartOpen} onOpenChange={setCartOpen} />
    </header>
  );
}
